import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, MOCK_MODE } from '../lib/api';
import type { LoginPasswordResult, Role, User } from '../lib/types';

interface AuthValue {
  user: User | null;
  /** /api/auth/me → permissions (owner için ['*']). */
  permissions: string[];
  loading: boolean;
  /** Yalnız mock modda: rol seçerek giriş. Gerçek modda SSO tam sayfa yönlendirme yapar. */
  loginSso: (role?: Role) => Promise<void>;
  loginPassword: (payload: { email: string; password: string }) => Promise<LoginPasswordResult>;
  verify2fa: (payload: { ticket: string; code: string }) => Promise<void>;
  logout: () => Promise<void>;
  can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);
const STORAGE_KEY = 'wa.mock.user';
/** Gerçek modda: bu tarayıcıda daha önce oturum açıldı mı? (giriş ekranında 401 önlemek için) */
const HAD_SESSION_KEY = 'wa.had-session';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const applySession = useCallback(async () => {
    const me = await api.me();
    setUser(me.user);
    setPermissions(me.permissions);
    sessionStorage.setItem(HAD_SESSION_KEY, '1');
    return me.user;
  }, []);

  useEffect(() => {
    let alive = true;

    async function boot() {
      try {
        if (MOCK_MODE) {
          const raw = sessionStorage.getItem(STORAGE_KEY);
          if (!raw) return;
          const parsed = JSON.parse(raw) as { role: Role };
          await api.loginSso(parsed.role);
        } else if (window.location.pathname === '/login' && !sessionStorage.getItem(HAD_SESSION_KEY)) {
          // Giriş ekranında ve daha önce oturum açılmamışsa /api/auth/me 401 döner;
          // gereksiz istek atmayız (konsol gürültüsü ve rate limit).
          return;
        }
        const me = await api.me();
        if (alive) {
          setUser(me.user);
          setPermissions(me.permissions);
        }
      } catch {
        if (alive) {
          setUser(null);
          setPermissions([]);
        }
      } finally {
        if (alive) setLoading(false);
      }
    }

    void boot();
    return () => {
      alive = false;
    };
  }, []);

  // API 401 döndürdüğünde oturumu düşür (süre dolması / iptal).
  useEffect(() => {
    const onUnauthorized = () => {
      setUser(null);
      setPermissions([]);
      if (MOCK_MODE) sessionStorage.removeItem(STORAGE_KEY);
    };
    window.addEventListener('wa:unauthorized', onUnauthorized);
    return () => window.removeEventListener('wa:unauthorized', onUnauthorized);
  }, []);

  const persistMock = useCallback((next: User | null) => {
    if (!MOCK_MODE) return;
    if (next) sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ role: next.role }));
    else sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  const loginSso = useCallback(
    async (role: Role = 'owner') => {
      const next = await api.loginSso(role);
      persistMock(next);
      await applySession();
    },
    [applySession, persistMock]
  );

  const loginPassword = useCallback(
    async (payload: { email: string; password: string }) => {
      const result = await api.loginPassword(payload);
      // 2FA gerekmiyorsa oturum hemen açılır; gerekliyse Login ekranı bilet ile devam eder.
      if (!result.twoFactorRequired) {
        persistMock(result.user);
        await applySession();
      }
      return result;
    },
    [applySession, persistMock]
  );

  const verify2fa = useCallback(
    async (payload: { ticket: string; code: string }) => {
      const next = await api.verify2fa(payload);
      persistMock(next);
      await applySession();
    },
    [applySession, persistMock]
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
      setPermissions([]);
      sessionStorage.removeItem(HAD_SESSION_KEY);
      persistMock(null);
    }
  }, [persistMock]);

  const can = useCallback(
    (permission: string) => permissions.includes('*') || permissions.includes(permission),
    [permissions]
  );

  const value = useMemo(
    () => ({ user, permissions, loading, loginSso, loginPassword, verify2fa, logout, can }),
    [user, permissions, loading, loginSso, loginPassword, verify2fa, logout, can]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth AuthProvider içinde kullanılmalı.');
  return context;
}
